import React, { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { useProjectStore } from '../../store/useProjectStore';
import * as api from '../../api/endpoints';
import type { ProjectUser, PageAssignment, AuthUser } from '../../api/endpoints';
import { withProjectQuery } from '../../api/projectContext';

interface TeamPanelProps {
  onClose: () => void;
}

// Format an ISO timestamp as a short, unambiguous added-on date.
function formatAddedDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function TeamPanel({ onClose }: TeamPanelProps) {
  const projectId = useProjectStore((s) => s.project?.project_id);
  const triage = useProjectStore((s) => s.triage);
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);
  const [allUsers, setAllUsers] = useState<AuthUser[]>([]);
  const [assignments, setAssignments] = useState<PageAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'team' | 'assignments'>('team');

  // Load data
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([
      api.pmGetProjectUsers(projectId).then(d => d && setProjectUsers(d)),
      api.authListUsers().then(d => d && setAllUsers(d)),
      api.pmGetAssignments(projectId).then(d => d && setAssignments(d)),
    ]).finally(() => setLoading(false));
  }, [projectId]);

  if (!projectId) return null;

  return (
    <div className="team-panel-overlay" onClick={onClose}>
      <div className="team-panel" onClick={e => e.stopPropagation()}>
        <div className="team-panel-header">
          <div className="team-panel-tabs">
            <button className={tab === 'team' ? 'active' : ''} onClick={() => setTab('team')}>
              Team
            </button>
            <button className={tab === 'assignments' ? 'active' : ''} onClick={() => setTab('assignments')}>
              Assignments
            </button>
          </div>
          <button className="team-panel-close" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div className="team-panel-loading">Loading...</div>
        ) : tab === 'team' ? (
          <TeamTab
            projectId={projectId}
            projectUsers={projectUsers}
            allUsers={allUsers}
            onUsersChanged={(users) => setProjectUsers(users)}
          />
        ) : (
          <AssignmentsTab
            projectId={projectId}
            projectUsers={projectUsers}
            assignments={assignments}
            triage={triage}
            onAssignmentsChanged={(a) => setAssignments(a)}
          />
        )}
      </div>
    </div>
  );
}

/* ── Team Tab ──────────────────────────────────────────────── */

function TeamTab({ projectId, projectUsers, allUsers, onUsersChanged }: {
  projectId: string;
  projectUsers: ProjectUser[];
  allUsers: AuthUser[];
  onUsersChanged: (users: ProjectUser[]) => void;
}) {
  const [addUserId, setAddUserId] = useState<number | ''>('');
  const [addRole, setAddRole] = useState('annotator');
  const [busy, setBusy] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [registerError, setRegisterError] = useState('');

  const availableUsers = allUsers.filter(u => !projectUsers.some(pu => pu.id === u.id));

  const handleGrant = async () => {
    if (!addUserId) return;
    setBusy(true);
    await api.pmGrantAccess(projectId, addUserId as number, addRole);
    const updated = await api.pmGetProjectUsers(projectId);
    if (updated) onUsersChanged(updated);
    setAddUserId('');
    setBusy(false);
  };

  const handleRevoke = async (userId: number) => {
    if (!confirm('Remove this user from the project?')) return;
    await api.pmRevokeAccess(projectId, userId);
    const updated = await api.pmGetProjectUsers(projectId);
    if (updated) onUsersChanged(updated);
  };

  const handleRegister = async () => {
    if (!newUsername.trim() || !newPassword.trim()) return;
    setRegisterError('');
    setBusy(true);
    const result = await api.authRegister(newUsername.trim(), newPassword.trim(), 'annotator');
    if (result) {
      // Auto-grant access to current project
      await api.pmGrantAccess(projectId, result.id, 'annotator');
      const updated = await api.pmGetProjectUsers(projectId);
      if (updated) onUsersChanged(updated);
      const updatedUsers = await api.authListUsers();
      // Re-fetch is handled via allUsers being passed from parent, but we can't update parent's allUsers directly
      setNewUsername('');
      setNewPassword('');
    } else {
      setRegisterError('Failed to create user (username may be taken)');
    }
    setBusy(false);
  };

  return (
    <div className="team-tab-content">
      {/* Current members */}
      <div className="team-section">
        <div className="team-section-label">Project Members</div>
        {projectUsers.length === 0 ? (
          <div className="dim">No users have access to this project yet.</div>
        ) : (
          <div className="team-user-list">
            {projectUsers.map(u => (
              <div key={u.id} className="team-user-row">
                <span className="team-user-name">{u.username}</span>
                <span className={`team-user-role role-${u.project_role}`}>{u.project_role}</span>
                {u.project_role !== 'owner' && (
                  <button className="team-user-remove" onClick={() => handleRevoke(u.id)} title="Remove from project">
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add existing user */}
      {availableUsers.length > 0 && (
        <div className="team-section">
          <div className="team-section-label">Add Existing User</div>
          <div className="team-add-row">
            <select value={addUserId} onChange={e => setAddUserId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Select user...</option>
              {availableUsers.map(u => (
                <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
              ))}
            </select>
            <select value={addRole} onChange={e => setAddRole(e.target.value)}>
              <option value="annotator">Annotator</option>
              <option value="owner">Owner</option>
            </select>
            <button onClick={handleGrant} disabled={!addUserId || busy}>Add</button>
          </div>
        </div>
      )}

      {/* Register new annotator */}
      <div className="team-section">
        <div className="team-section-label">Create New Annotator</div>
        <div className="team-add-row">
          <input
            type="text"
            placeholder="Username"
            value={newUsername}
            onChange={e => setNewUsername(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleRegister()}
          />
          <button onClick={handleRegister} disabled={busy || !newUsername.trim() || !newPassword.trim()}>
            Create & Add
          </button>
        </div>
        {registerError && <div className="team-error">{registerError}</div>}
      </div>
    </div>
  );
}

/* ── Assignments Tab ──────────────────────────────────────── */

function AssignmentsTab({ projectId, projectUsers, assignments, triage, onAssignmentsChanged }: {
  projectId: string;
  projectUsers: ProjectUser[];
  assignments: PageAssignment[];
  triage: any;
  onAssignmentsChanged: (a: PageAssignment[]) => void;
}) {
  const [assignUserId, setAssignUserId] = useState<number | ''>('');
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [filterUser, setFilterUser] = useState<number | 'all'>('all');
  const [pageNames, setPageNames] = useState<Record<string, string>>({});
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [suggestedAssignments, setSuggestedAssignments] = useState<Record<string, number> | null>(null);
  // Per-file S3 added date (basename → ISO), to show how new each file is.
  const [fileAddedDates, setFileAddedDates] = useState<Record<string, string>>({});

  useEffect(() => {
    api.getFileAddedDates().then((d) => setFileAddedDates(d || {}));
  }, [projectId]);

  // Doc types shown by default (visual / annotatable pages)
  const DEFAULT_TYPES = useMemo(() => new Set([
    'floor_plan_power', 'floor_plan_lighting', 'floor_plan_data',
    'detail_drawing', 'layout_plan', 'circuit_diagram', 'wiring_diagram',
  ]), []);

  const [enabledTypes, setEnabledTypes] = useState<Set<string>>(new Set(DEFAULT_TYPES));

  // All pages from triage (unfiltered)
  const allPages = useMemo(() => {
    if (!triage?.classifications) return [];
    return triage.classifications.map((c: any) => ({
      file: c.file || c.filename,
      page: c.page,
      doc_type: c.doc_type,
      title: c.title || '',
    }));
  }, [triage]);

  // Unique doc types in this project
  const allDocTypes = useMemo(() => {
    const types = new Set<string>();
    for (const p of allPages) if (p.doc_type) types.add(p.doc_type);
    return [...types].sort();
  }, [allPages]);

  // Filtered pages by enabled doc types
  const pages = useMemo(() => {
    if (showAllTypes) return allPages;
    return allPages.filter((p: any) => enabledTypes.has(p.doc_type));
  }, [allPages, enabledTypes, showAllTypes]);

  const toggleDocType = (dt: string) => {
    setEnabledTypes(prev => {
      const next = new Set(prev);
      if (next.has(dt)) next.delete(dt);
      else next.add(dt);
      return next;
    });
  };

  // Group pages by file
  const fileGroups = useMemo(() => {
    const groups: Record<string, typeof pages> = {};
    for (const p of pages) {
      (groups[p.file] ??= []).push(p);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [pages]);

  // Assignment lookup: "file:page" → assignment
  const assignmentMap = useMemo(() => {
    const map: Record<string, PageAssignment> = {};
    for (const a of assignments) {
      map[`${a.file}:${a.page}`] = a;
    }
    return map;
  }, [assignments]);

  const annotators = projectUsers.filter(u => u.project_role === 'annotator');

  const togglePage = (key: string) => {
    setSelectedPages(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleFile = (file: string) => {
    const filePages = pages.filter((p: any) => p.file === file)
      .map((p: any) => `${p.file}:${p.page}`)
      .filter((k: string) => !assignmentMap[k]);
    setSelectedPages(prev => {
      const next = new Set(prev);
      const allSelected = filePages.every((k: string) => next.has(k));
      if (allSelected) {
        filePages.forEach((k: string) => next.delete(k));
      } else {
        filePages.forEach((k: string) => next.add(k));
      }
      return next;
    });
  };

  // Suggest: distribute unassigned pages evenly across annotators
  const handleSuggest = () => {
    const unassigned = pages
      .map((p: any) => `${p.file}:${p.page}`)
      .filter((k: string) => !assignmentMap[k]);
    if (unassigned.length === 0 || annotators.length === 0) return;

    const suggested: Record<string, number> = {};
    unassigned.forEach((key: string, i: number) => {
      suggested[key] = annotators[i % annotators.length].id;
    });
    setSuggestedAssignments(suggested);
    // Select all suggested pages
    setSelectedPages(new Set(unassigned));
  };

  const handleApplySuggestion = async () => {
    if (!suggestedAssignments) return;
    setBusy(true);
    // Group by user
    const byUser: Record<number, { file: string; page: number; doc_type?: string; name?: string }[]> = {};
    for (const [key, userId] of Object.entries(suggestedAssignments)) {
      const [file, pageStr] = key.split(':');
      // Search in allPages (unfiltered) to find pages that might be hidden by type filter
      const pg = allPages.find((p: any) => p.file === file && p.page === Number(pageStr));
      const name = pageNames[key] || undefined;
      (byUser[userId] ??= []).push({ file, page: Number(pageStr), doc_type: pg?.doc_type, name });
    }
    let assigned = 0;
    let failed = 0;
    for (const [userId, pageList] of Object.entries(byUser)) {
      try {
        await api.pmAssignPages(projectId, Number(userId), pageList);
        assigned += pageList.length;
      } catch (e) {
        failed += pageList.length;
        console.error('Failed to assign to user', userId, e);
      }
    }
    if (failed > 0) {
      alert(`Assigned ${assigned} pages, ${failed} failed. Check console for details.`);
    }
    const updated = await api.pmGetAssignments(projectId);
    if (updated) onAssignmentsChanged(updated);
    setSelectedPages(new Set());
    setSuggestedAssignments(null);
    setBusy(false);
  };

  // Preview: show fast thumbnail on click
  const handlePreview = (file: string, page: number) => {
    const key = `${file}:${page}`;
    if (previewKey === key) { setPreviewKey(null); return; }
    setPreviewKey(key);
    setPreviewUrl(withProjectQuery(`/api/page/thumbnail?file=${encodeURIComponent(file)}&page=${page}`));
    setPreviewLoading(false);
  };

  const handleAssign = async () => {
    if (!assignUserId || selectedPages.size === 0) return;
    setBusy(true);
    const pageList = [...selectedPages].map(key => {
      const [file, pageStr] = key.split(':');
      const pg = pages.find((p: any) => p.file === file && p.page === Number(pageStr));
      const name = pageNames[key] || undefined;
      return { file, page: Number(pageStr), doc_type: pg?.doc_type, name };
    });
    await api.pmAssignPages(projectId, assignUserId as number, pageList);
    const updated = await api.pmGetAssignments(projectId);
    if (updated) onAssignmentsChanged(updated);
    setSelectedPages(new Set());
    setPageNames({});
    setBusy(false);
  };

  const filteredAssignments = filterUser === 'all'
    ? assignments
    : assignments.filter(a => a.user_id === filterUser);

  return (
    <div className="assignments-tab-content">
      {/* Assign controls */}
      <div className="assign-controls">
        <select value={assignUserId} onChange={e => setAssignUserId(e.target.value ? Number(e.target.value) : '')}>
          <option value="">Assign to...</option>
          {annotators.map(u => (
            <option key={u.id} value={u.id}>{u.username}</option>
          ))}
        </select>
        <button
          onClick={handleAssign}
          disabled={busy || !assignUserId || selectedPages.size === 0}
        >
          Assign {selectedPages.size} page{selectedPages.size !== 1 ? 's' : ''}
        </button>
        <button
          onClick={suggestedAssignments ? handleApplySuggestion : handleSuggest}
          disabled={busy || annotators.length === 0}
          className={suggestedAssignments ? 'assign-apply-btn' : ''}
          title={suggestedAssignments
            ? 'Apply the suggested distribution'
            : 'Auto-distribute unassigned pages evenly across annotators'}
        >
          {suggestedAssignments ? `Apply suggestion (${Object.keys(suggestedAssignments).length} pages)` : 'Suggest'}
        </button>
        {suggestedAssignments && (
          <button onClick={() => { setSuggestedAssignments(null); setSelectedPages(new Set()); }} className="dim">
            Clear
          </button>
        )}
        <span className="dim" style={{ marginLeft: 'auto' }}>
          Filter:
          <select value={filterUser} onChange={e => setFilterUser(e.target.value === 'all' ? 'all' : Number(e.target.value))} style={{ marginLeft: 4 }}>
            <option value="all">All</option>
            {annotators.map(u => (
              <option key={u.id} value={u.id}>{u.username}</option>
            ))}
          </select>
        </span>
      </div>

      {/* Doc type filter */}
      {allDocTypes.length > 0 && (
        <div className="assign-type-filter">
          <label className="assign-type-toggle">
            <input type="checkbox" checked={showAllTypes} onChange={() => setShowAllTypes(!showAllTypes)} />
            Show all page types
          </label>
          {!showAllTypes && (
            <div className="assign-type-chips">
              {allDocTypes.map(dt => {
                const count = allPages.filter((p: any) => p.doc_type === dt).length;
                return (
                  <button
                    key={dt}
                    className={`assign-type-chip ${enabledTypes.has(dt) ? 'active' : ''}`}
                    onClick={() => toggleDocType(dt)}
                    title={`${count} pages`}
                  >
                    {dt.replace(/_/g, ' ')} ({count})
                  </button>
                );
              })}
            </div>
          )}
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Showing {pages.length} of {allPages.length} pages
          </div>
        </div>
      )}

      {/* Page list grouped by file */}
      {pages.length === 0 ? (
        <div className="dim" style={{ padding: 16 }}>
          {allPages.length === 0
            ? 'No triage data — run the pipeline first to see pages.'
            : 'No pages match the selected types. Toggle more types above.'}
        </div>
      ) : (
        <div className="assign-file-list">
          {fileGroups.map(([file, filePages]) => {
            const fileKeys = filePages.map((p: any) => `${p.file}:${p.page}`);
            const allSelected = fileKeys.every((k: string) => selectedPages.has(k));
            return (
              <div key={file} className="assign-file-group">
                <div className="assign-file-header" onClick={() => toggleFile(file)}>
                  <input type="checkbox" checked={allSelected} readOnly />
                  <span className="assign-file-name">{file}</span>
                  <span className="dim">({filePages.length} pages)</span>
                  {(() => {
                    const base = file.split('/').pop() || file;
                    const addedAt = fileAddedDates[file] || fileAddedDates[base];
                    return addedAt ? (
                      <span className="dim assign-file-added" title={`Added to S3 on ${new Date(addedAt).toLocaleString()}`}>
                        · Added {formatAddedDate(addedAt)}
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="assign-page-rows">
                  {filePages.map((p: any) => {
                    const key = `${p.file}:${p.page}`;
                    const assignment = assignmentMap[key];
                    const isFiltered = filterUser !== 'all' && assignment && assignment.user_id !== filterUser;
                    if (isFiltered) return null;
                    const defaultName = file.replace(/\.\w+$/i, '') + ` p.${p.page}`;
                    const isAssigned = !!assignment;
                    const suggestedUser = suggestedAssignments?.[key];
                    const suggestedName = suggestedUser ? annotators.find(a => a.id === suggestedUser)?.username : null;
                    return (<React.Fragment key={key}>
                      <div className={`assign-page-row ${isAssigned ? 'assigned' : ''} ${suggestedUser ? 'suggested' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedPages.has(key)}
                          onChange={() => togglePage(key)}
                          disabled={isAssigned}
                        />
                        <button
                          className="assign-preview-btn"
                          onClick={(e) => { e.stopPropagation(); handlePreview(p.file, p.page); }}
                          title="Preview page"
                        >
                          {previewKey === key ? '▼' : '▶'}
                        </button>
                        <span className="assign-page-num">p.{p.page}</span>
                        <input
                          className="assign-page-name-input"
                          type="text"
                          value={pageNames[key] ?? defaultName}
                          onChange={e => setPageNames(prev => ({ ...prev, [key]: e.target.value }))}
                          title="Assignment name"
                        />
                        <span className={`assign-doc-type dt-${p.doc_type}`}>{p.doc_type}</span>
                        {p.title && <span className="dim assign-page-title">{p.title}</span>}
                        {assignment && (
                          <span className={`assign-badge status-${assignment.status}`}>
                            {assignment.username || `user#${assignment.user_id}`}
                            {assignment.status !== 'assigned' && ` (${assignment.status})`}
                          </span>
                        )}
                        {suggestedName && !isAssigned && (
                          <span className="assign-badge assign-suggested">
                            → {suggestedName.split('@')[0]}
                          </span>
                        )}
                      </div>
                      {previewKey === key && (
                        <div className="assign-preview-pane">
                          {previewLoading ? (
                            <div className="dim" style={{ padding: 20, textAlign: 'center' }}>Loading preview...</div>
                          ) : previewUrl ? (
                            <img src={previewUrl} alt={`Preview of ${p.file} p.${p.page}`} className="assign-preview-img" />
                          ) : (
                            <div className="dim" style={{ padding: 20, textAlign: 'center' }}>Preview not available</div>
                          )}
                        </div>
                      )}
                    </React.Fragment>);
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
